import { t } from "@lingui/core/macro";
import { assertNever, clsx, EMPTY_STRING } from "@regrello/core-utils";
import type React from "react";
import { useMemo } from "react";
import { RegrelloTooltip, type RegrelloTooltipProps } from "../../overlay/tooltip/RegrelloTooltip";
import type { RegrelloIntent } from "../../tokens/enums/regrelloIntent";
import { RegrelloShape } from "../../tokens/enums/regrelloShape";
import { RegrelloSize } from "../../tokens/enums/regrelloSize";
import { RegrelloTypography } from "../../tokens/typography/RegrelloTypography";
import { RegrelloIcon, type RegrelloIconName } from "../../visual/icons/RegrelloIcon";
import { RegrelloBetaLabel } from "../betaLabel/RegrelloBetaLabel";

export interface RegrelloAvatarProps {
  /**
   * Whether to apply a grayscale overlay to the avatar icon to indicate they are deactivated in the
   * workspace.
   *
   * @default false
   */
  deactivated?: boolean;

  /**
   * Whether this party is idle, indicating they are online but dormant. This can happen if they
   * have the Regrello tab open but haven't done anything in it in some time—or if they may have
   * even unfocused the page to do something else.
   *
   * @default false
   */
  idle?: boolean;

  /**
   * A visual indicator that this party is out of the ordinary in some way. Currently, all
   * options here will show an icon that reveals an explanatory tooltip on hover.
   *
   * - `"inactive"`: Indicates that the party has been deleted.
   * - `"muted"`: Indicates that the party has muted all email notifications.
   */
  indicator?: "inactive" | "muted";

  /**
   * Whether this avatar is being rendered inline with other text. This will cause the avatar to
   * display in flow with the surrounding text.
   *
   * __Note:__ This is really only meaningful for `x-small` and `small` sizes. If you need to render
   * `medium` and above inline with surrounding text, reconsider your design before using this prop.
   *
   * @default false
   */
  inline?: boolean;

  /**
   * Whether to bold the text of the avatar.
   *
   * @default false
   */
  isEmphasized?: boolean;

  /**
   * Whether to visually emphasize the avatar if `inactive` is `true`.
   *
   * @default false
   */
  isHighlightedWhenInactive?: boolean;

  /**
   * When this avatar is being rendered as part of a stack of avatars and is not the first in the
   * stack, set this to `true` to show a white left border for visually delineating it from the
   * other avatars in the stack.
   *
   * @default false
   */
  isStackedBordersEnabled?: boolean;

  /** Ref to the root element. */
  ref?: React.Ref<HTMLElement>;

  /**
   * Whether the avatar corresponds to an internal or external party. Renders slightly different
   * styling to differentiate the two. `pending` renders the avatar as a hollow circle with dotted
   * outline.
   *
   * @default "internal"
   */
  scope?: "external" | "internal" | "limited" | "placeholder" | "unknown";

  /** Helper text to render beneath the main {@see text}. Will be more muted than the main text. */
  secondaryText?: string;

  /**
   * A class name to apply to the email text. This is offered for certain very rare use cases in
   * which the color needs to be customized; if you aren't absolutely positive you need this, you
   * should avoid it.
   */
  secondaryTextClassName?: string;

  /**
   * The shape of the visual.
   *
   * @default RegrelloShape.CIRCLE
   */
  shape?: RegrelloShape;

  /**
   * (Experimental) Whether to show a background if this is an AI agent. Ignored if this party is
   * not an AI agent.
   *
   * @default false
   */
  showBackgroundForAiAgents?: boolean;

  /**
   * Whether to show the "Beta" chip next to the text.
   *
   * @default false
   */
  showBetaLabel?: boolean;

  /**
   * The size of the avatar. Passing `RegrelloSize.X_SMALL` will render in a manner appropriate for
   * inline usage (as a chip). Passing `RegrelloSize.X_LARGE` will show only the visual, not the
   * {@see text} or {@see secondaryText}.
   *
   * @default RegrelloSize.MEDIUM
   */
  size?: RegrelloSize;

  /** The primary identifying text to display (e.g., the display name of the party). */
  text?: string;

  /** Tooltip to display when hovering over the user. Useful to provide unique identifier of the user. */
  tooltip?: string;

  /** Overrides the default maxWidth of the tooltip. */
  tooltipMaxWidth?: RegrelloTooltipProps["maxWidth"];

  /**
   * Whether to stack avatars with the topmost avatar being on the left ("start" side) or right
   * ("end" side).
   *
   * @default "end"
   */
  topmostSide?: "start" | "end";

  /**
   * A square-ish or circular visual element to represent this avatar (e.g., an image or an enclosed
   * visualization of the party's initials).
   */
  visual:
    | { type: "initials"; text: string }
    | { type: "image"; alt: string; src: string }
    | {
        type: "icon";
        iconName: RegrelloIconName;
        intent?: Extract<RegrelloIntent, "primary" | "danger" | "special">;
        isFullSize?: boolean;

        /**
         * Whether the icon should be rendered without a visual wrapping container (e.g., bubble /
         * box around it). This applies the color styles directly to the icon versus its parent and
         * makes the icon default to the size of the avatar itself.
         *
         * @default false
         */
        isVisuallyUnwrapped?: boolean;
      };
}

/** Represents a user or team in Regrello. */
export function RegrelloAvatar({
  deactivated: isDeactivated = false,
  indicator,
  inline: isInline = false,
  isEmphasized = false,
  isHighlightedWhenInactive,
  idle: isIdle = false,
  isStackedBordersEnabled = false,
  ref,
  scope = "internal",
  secondaryText,
  secondaryTextClassName,
  shape = RegrelloShape.CIRCLE,
  showBackgroundForAiAgents = false,
  showBetaLabel = false,
  size = RegrelloSize.MEDIUM,
  text,
  tooltip = EMPTY_STRING,
  tooltipMaxWidth,
  // eslint-disable-next-line lingui/no-unlocalized-strings
  topmostSide = "end",
  visual,
}: RegrelloAvatarProps) {
  const visualElement = useMemo(() => {
    const visualClassName = clsx(
      `
        box-border

        flex
        items-center
        justify-center

        rounded-full

        w-5
        h-5
        overflow-hidden
      `,
      {
        // Background
        "bg-primary-icon": !isDeactivated,
        "bg-neutral-icon": isDeactivated,

        // Size
        "w-4 h-4": size === "x-small",
        "w-5 h-5": size === "small",
        "w-6 h-6": size === "medium",
        "w-9 h-9": size === "large",
        "w-10 h-10": size === "x-large",
      },
    );

    switch (visual.type) {
      case "image":
        return (
          <div className={visualClassName}>
            <img alt={visual.alt} className="w-full h-full" src={visual.src} />
          </div>
        );
      case "initials":
        return (
          <div
            className={clsx(visualClassName, {
              // Default state
              "bg-background border border-primary-icon": scope === "external",
              "bg-warning-soft border border-warning-borderInteractiveStrong": scope === "limited",
              "bg-background border border-dashed border-neutral-textMuted": scope === "unknown", // HACKHACK (clewis): Non-standard color usage of "textMuted" for a border, because "borderInteractiveStrong" feels too light for this dashed border.
              "bg-primary-softPressed": isIdle,

              // Deactivated state
              "border-neutral-icon": isDeactivated && scope === "external",
              "bg-neutral-soft border-neutral-borderInteractiveStrong": isDeactivated && scope === "limited",
              "border-neutral-textMuted": isDeactivated && scope === "unknown",
              "bg-neutral-softPressed": isDeactivated && isIdle,

              // Shape
              "rounded-full": shape === "circle",
              rounded: shape === "rectangle",

              // Stacked borders
              "border-r border-background": topmostSide === "start" && isStackedBordersEnabled && scope === "internal",
              "border-l border-background": topmostSide === "end" && isStackedBordersEnabled && scope === "internal",
            })}
          >
            <div
              className={clsx("font-semibold leading-4", {
                "text-textContrast": scope === "internal" || scope === "placeholder",
                "text-primary-icon": scope === "external",
                "text-warning-icon": scope === "limited",
                "text-textMuted": scope === "unknown",

                // Deactivated state
                "text-neutral-icon":
                  isDeactivated && scope !== "unknown" && scope !== "placeholder" && scope !== "internal",

                "text-[8px]": size === "x-small",
                "text-[10px]": size === "small",
                // Needs a slight nudge down to optically center.
                "text-[12px] mt-px": size === "medium",
                "text-[16px] mt-px": size === "large",
                "text-[18px] mt-px": size === "x-large",
              })}
            >
              {scope === "unknown" ? "?" : visual.text}
            </div>
          </div>
        );
      case "icon":
        if (visual.isVisuallyUnwrapped) {
          return (
            <RegrelloIcon
              className={clsx(visualClassName, {
                "bg-transparent text-textMuted overflow-visible": true,
                "text-primary-icon": visual.intent === "primary",
                "text-danger-icon": visual.intent === "danger",
                "text-neutral-icon": isDeactivated,
              })}
              iconName={visual.iconName}
              intent={visual.intent}
              size="inherit"
            />
          );
        }
        return (
          <div
            className={clsx(visualClassName, {
              // (clewis): I acknowledge that this is a non-standard use of this semantic color,
              // but it's better than -icon because we have contrast text showing in the avatar
              // circle, and we need the contrast ratio to be high enough for the text to be
              // readable.
              "bg-neutral-iconMuted text-textContrast": !visual.isFullSize,
              // (zstanik): For some reason the path within the SVG was less than 100% size, making it
              // look smaller. Should likely be redrawn, this is a quick fix.
              "bg-background text-textMuted [&_svg]:scale-110": visual.isFullSize,
              // (clewis): Cheat a bit because we need the icon to be smaller than the smallest
              // official icon size.
              "[&_svg]:scale-[0.7]": size === "x-small" && !visual.isFullSize,

              // Intent
              "bg-primary-icon text-textContrast": visual.intent === "primary",
              "bg-danger-icon text-textContrast": visual.intent === "danger",
              "bg-special-icon text-textContrast": visual.intent === "special",

              // Deactivated state
              "bg-neutral-icon text-textContrast": isDeactivated,
            })}
          >
            <RegrelloIcon
              iconName={visual.iconName}
              size={size === "x-small" || size === "small" || size === "medium" ? "x-small" : undefined}
            />
          </div>
        );

      default:
        assertNever(visual);
    }
  }, [isDeactivated, isIdle, isStackedBordersEnabled, scope, shape, size, topmostSide, visual]);

  const isSizeXSmall = size === "x-small";
  const isSizeSmall = size === "small";
  const isSizeXLarge = size === "x-large";

  const isPrimaryTextShown = !isSizeXLarge && text != null;
  const isSecondaryTextShown = !isSizeXSmall && !isSizeSmall && !isSizeXLarge && secondaryText != null;
  const isAnyTextShown = isPrimaryTextShown || isSecondaryTextShown;

  const isInactive = indicator === "inactive";

  const tooltipTitle = scope === "unknown" ? t`Added via task but not a member of this workspace` : tooltip;

  function getIndicatorIconName(indicatorValue: "inactive" | "muted"): RegrelloIconName {
    switch (indicatorValue) {
      case "inactive":
        return "alert";
      case "muted":
        return "bell-muted";
      default:
        assertNever(indicatorValue);
    }
  }

  const isTextHidden = text == null;

  return (
    <RegrelloTooltip
      content={tooltipTitle}
      disabled={tooltipTitle.length === 0}
      maxWidth={tooltipMaxWidth}
      openDelay="long"
      variant="popover"
    >
      {/* (hchen): Wrap with `div` to support tooltip. the root div has it's own ref handling logic. */}
      {/*
       * (clewis): This vertical-alignment ensures that the avatar's text will share a baseline with
       * surrounding text of the same size.
       */}
      <div
        className={clsx("min-w-0", { flex: !isInline, "inline-flex align-bottom": isInline })}
        data-tooltip={tooltipTitle}
      >
        <div
          ref={ref != null ? (ref as React.Ref<HTMLDivElement>) : undefined}
          // Set 0 minWidth for correct `textOverflow: ellipsis` behavior;
          className={clsx("flex items-center min-w-0", {
            [`
                bg-warning-soft
                rounded
                px-1
                py-0.5
                mx-[-1]
                my-[-0.5]
              `]: isInactive && isHighlightedWhenInactive,
            [`
                bg-special-soft
                text-special-text
                rounded
                px-1
                py-0.5
                mx-[-1]
                -my-1
              `]: showBackgroundForAiAgents && !isTextHidden && visual.type === "icon" && visual.intent === "special",
            "p-0.5 m-0.5": isInactive && isHighlightedWhenInactive && !isAnyTextShown,
          })}
        >
          <div className="flex-none">{visualElement}</div>
          {isAnyTextShown && (
            <div
              className={clsx("min-w-0 flex-auto", {
                "ml-1 flex items-center": size === "x-small" || size === "small",
                "ml-1.5": size === "medium",
                "ml-2": size === "large",
                italic: scope === "placeholder" || isDeactivated,
                "font-semibold": isEmphasized,
                "text-textMuted": scope === "placeholder",
              })}
            >
              {isPrimaryTextShown && (
                <div className="flex">
                  <RegrelloTypography
                    className={clsx({ "font-semibold": isEmphasized })}
                    noWrap={true}
                    variant={size === "x-small" ? "body-xs" : "body"}
                  >
                    {text}
                  </RegrelloTypography>
                  {showBetaLabel && <RegrelloBetaLabel className="ml-2" />}
                </div>
              )}
              {isSecondaryTextShown && (
                <RegrelloTypography className={secondaryTextClassName} muted={true} noWrap={true} variant="body-xs">
                  {secondaryText}
                </RegrelloTypography>
              )}
            </div>
          )}
          {indicator != null && size !== "x-large" && isAnyTextShown && (
            <div
              className={clsx("ml-1", {
                "text-warning-textMuted": isHighlightedWhenInactive,
                "text-neutral-textMuted": isDeactivated,
                "right-0.5": isSizeXSmall,
              })}
            >
              <RegrelloIcon
                iconName={getIndicatorIconName(indicator)}
                intent={isHighlightedWhenInactive ? undefined : "neutral"}
                size="x-small"
              />
            </div>
          )}
        </div>
      </div>
    </RegrelloTooltip>
  );
}
